// Task Template Types for JavaScript
// Phase 3 - Task Management System

import { Timestamp } from 'firebase/firestore';

// ==================== TEMPLATE DEFAULTS ====================

/**
 * Create default task template
 */
export const createDefaultTaskTemplate = () => {
  const now = Timestamp.now();
  
  return {
    // IDENTIFICATION
    id: null,
    name: '',
    description: '',
    taskType: '',  // From nomenclature
    
    // MULTI-PROTOCOL SUPPORT
    protocols: [],
    
    // DEFAULT CONTEXT
    defaultContext: {
      patientCondition: null,
      isolationLevel: null,
      equipmentTypes: [],
      procedureType: null,
      environmentalRisk: null,
      contextNotes: ''
    },
    
    // ASSIGNMENT RULES
    assignmentRules: {
      assignmentType: 'direct',  // From nomenclature
      requiredRoles: [],  // Array of role IDs from roles collection
      requiredCertifications: [],
      preferredShift: null,
      workloadThreshold: 5,
      assignedToGroup: null,
      workloadBalancing: null
    },
    
    // TIMING RULES
    timingRules: {
      estimatedDuration: 30,
      durationUnit: 'minutes',
      mustStartWithin: null,
      mustCompleteWithin: null,
      timeUnit: 'hours',
      allowExtension: true,
      extensionApprovalRequired: false
    },
    
    // ESCALATION RULES
    escalationRules: [],
    
    // DEFAULTS
    defaultPriority: 'medium',
    defaultUrgency: 'routine',
    
    // METADATA
    isActive: true,
    usageCount: 0,
    lastUsed: null,
    
    // AUDIT
    createdBy: '',
    createdAt: now,
    updatedBy: '',
    updatedAt: now
  };
};

/**
 * Create protocol reference for template
 */
export const createProtocolReference = (protocolId, protocolName, sequenceOrder = 1) => {
  return {
    protocolId,
    protocolName,
    sequenceOrder,
    isRequired: true,
    canSkip: false,
    skipRequiresApproval: false
  };
};

/**
 * Create escalation rule
 */
export const createEscalationRule = (condition, action, recipients = [], delayMinutes = 30) => {
  return {
    condition,  // 'not_started_within_X', 'not_completed_on_time', etc.
    action,  // 'notify_supervisor', 'escalate_to_manager', 'reassign', etc.
    recipients,  // User IDs or group IDs
    delayMinutes
  };
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Validate task template
 */
export const validateTaskTemplate = (template) => {
  const errors = [];
  
  if (!template.name || template.name.trim() === '') {
    errors.push('Template name is required');
  }
  
  if (!template.taskType) {
    errors.push('Task type is required');
  }
  
  if (template.protocols.length === 0) {
    errors.push('At least one protocol is required');
  }
  
  if (template.assignmentRules.assignmentType === 'group_based' && !template.assignmentRules.assignedToGroup) {
    errors.push('Group must be selected for group-based assignment');
  }
  
  if (template.timingRules.estimatedDuration <= 0) {
    errors.push('Estimated duration must be greater than 0');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Calculate total estimated duration for multi-protocol template
 */
export const calculateTemplateDuration = (protocols) => {
  // This would need to fetch protocol data to get actual durations
  // For now, return the template's estimated duration
  return 0;
};

/**
 * Get template usage statistics
 */
export const getTemplateStats = (template) => {
  return {
    usageCount: template.usageCount || 0,
    lastUsed: template.lastUsed,
    isActive: template.isActive,
    protocolCount: template.protocols.length
  };
};

// ==================== UI OPTIONS ====================

export const ESCALATION_CONDITIONS = [
  { value: 'not_started_within_30', label: 'Not started within 30 minutes' },
  { value: 'not_started_within_1_hour', label: 'Not started within 1 hour' },
  { value: 'not_started_within_2_hours', label: 'Not started within 2 hours' },
  { value: 'not_completed_on_time', label: 'Not completed on time' },
  { value: 'overdue_30', label: 'Overdue by 30 minutes' },
  { value: 'overdue_1_hour', label: 'Overdue by 1 hour' }
];

export const ESCALATION_ACTIONS = [
  { value: 'notify_supervisor', label: 'Notify Supervisor' },
  { value: 'notify_manager', label: 'Notify Manager' },
  { value: 'escalate_to_manager', label: 'Escalate to Manager' },
  { value: 'reassign', label: 'Reassign Task' },
  { value: 'increase_priority', label: 'Increase Priority' },
  { value: 'add_resources', label: 'Add Resources' }
];

export const DURATION_UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' }
];

export const TIME_UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' }
];

